import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import { useT } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/en.ts";
import { kindLabelKey } from "../i18n/fieldLabels.ts";
import { ChevronIcon } from "./ChevronIcon.tsx";
import { EyeIcon } from "./EyeIcon.tsx";
import { EyeOffIcon } from "./EyeOffIcon.tsx";
import { NodeIcon } from "./NodeIcon.tsx";
import { RaingageIcon } from "./RaingageIcon.tsx";
import { SubcatchmentIcon } from "./SubcatchmentIcon.tsx";
import { AddConduitIcon as LinkIcon } from "./AddConduitIcon.tsx";
import { VirtualList } from "./VirtualList.tsx";
import { isMultiSelected, LINK_KIND_ORDER, NODE_KIND_ORDER } from "./useNetworkMapState.ts";
import {
  LINK_COLOR,
  LINK_DASH,
  NODE_COLOR,
  RAINGAGE_COLOR,
  SUBCATCHMENT_COLOR,
} from "./mapTheme.ts";

/** This project's test environment has no `ResizeObserver`, so tests render at this constant instead. A real browser overwrites it via `useAvailableHeight` below on its first layout pass. */
const FALLBACK_LIST_HEIGHT = 600;

/**
 * Fills the sidebar's real available height so the one `VirtualList` below
 * covers the whole tree with exactly one scrollbar, instead of guessing a
 * fixed pixel constant. `ResizeObserver` tracks every layout change that can
 * resize this panel (window resize, a banner appearing above it) — not just
 * a `resize` event, which none of those fire.
 */
function useAvailableHeight() {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(FALLBACK_LIST_HEIGHT);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.height;
      if (measured && measured > 0) setHeight(measured);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, height] as const;
}

/**
 * `row.kind` (used below to label a "kind" header row) only ever comes from
 * `NODE_KIND_ORDER`/`LINK_KIND_ORDER` — `pushKindGroupCategory` further down
 * is the only place that builds a "kind" row, and both orders are fully
 * covered by `fieldLabels.ts`'s own `KIND_LABEL_KEYS`. That shared
 * `kindLabelKey`'s `| null` (for an arbitrary, unvalidated string) is
 * unreachable here, so it's asserted non-null once rather than threading a
 * fallback no caller could ever actually hit.
 */
function rowKindLabelKey(kind: string): TextKey {
  return kindLabelKey(kind)!;
}

/**
 * Same shape-per-kind convention as NetworkMap.tsx's SVG rendering (circle/
 * triangle/square/diamond), sized for legend use in the element tree.
 *
 * Audit finding: giving every shape the same numeric side/radius on the
 * previous 12×12 grid made them look visibly different sizes — the
 * junction circle read markedly smaller than the storage square because
 * a circle only covers ~π/4 (79 %) of its bounding box, and an upright
 * triangle even less. All four shapes now share the same 14×14 SVG
 * bounding box, but each shape's own dimensions are tuned so its
 * visible mass matches the others: the circle nearly fills the box,
 * the square gets a slight step down (squares read heavier at equal
 * width), and both triangular shapes get a step up to compensate for
 * their tapered corners. Common icon-design rule (Chermayeff /
 * Vignelli / Material's icon-grid convention).
 */
function NodeKindSwatch({ kind, color }: { kind: string; color: string }) {
  switch (kind) {
    case "junction":
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <circle cx="7" cy="7" r="6.5" fill={color} />
        </svg>
      );
    case "outfall":
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <polygon points="7,1.5 13.5,13 0.5,13" fill={color} />
        </svg>
      );
    case "storage":
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <rect x="1.75" y="1.75" width="10.5" height="10.5" fill={color} />
        </svg>
      );
    case "divider":
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <polygon points="7,0.5 13.5,7 7,13.5 0.5,7" fill={color} />
        </svg>
      );
    default:
      return null;
  }
}

/** A short colored line segment — every link kind renders as a line on both maps, only its color differs, so there's no per-kind shape here. */
/** `dash` mirrors the map's own per-kind stroke-dasharray (LINK_DASH, useNetworkMapState.ts) — same WCAG 1.4.1 reasoning: this legend must not rely on color alone either. `strokeLinecap="round"` (kept from before) would round off short dash segments into near-dots, so it's dropped whenever a dash pattern is present — butt caps read the intended pattern shapes correctly. */
function LinkKindSwatch({ color, dash }: { color: string; dash?: string }) {
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true">
      <line
        x1="1"
        y1="6"
        x2="15"
        y2="6"
        stroke={color}
        strokeWidth="3"
        strokeLinecap={dash ? "butt" : "round"}
        strokeDasharray={dash}
      />
    </svg>
  );
}

/**
 * Shared markup for both the "category" and "kind" header rows `renderRow`
 * below renders — same chevron-toggle button plus eye/visibility button
 * layout either way. The icon/swatch + label content between them differs
 * enough per row type (an `<h3>` heading with the category's own icon
 * color vs. a plain labeled swatch, no heading semantics, for a kind
 * subgroup) that it's left to the caller as `children` rather than forced
 * into shared props.
 */
function ElementBrowserHeaderRow({
  rowClassName,
  toggleClassName,
  toggleAriaLabel,
  expanded,
  onToggleExpanded,
  visible,
  onToggleVisible,
  children,
}: {
  rowClassName: string;
  toggleClassName: string;
  toggleAriaLabel: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  visible: boolean;
  onToggleVisible: () => void;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <div className={rowClassName}>
      <button
        className={toggleClassName}
        onClick={onToggleExpanded}
        aria-label={toggleAriaLabel}
        aria-expanded={expanded}
      >
        <ChevronIcon expanded={expanded} />
        {children}
      </button>
      <button
        className="button-icon element-browser-visibility"
        onClick={onToggleVisible}
        aria-label={t("elementBrowser.toggleOnMap")}
        title={t("elementBrowser.toggleOnMap")}
      >
        {visible ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

type Item = { name: string; ref: ElementRef };

/**
 * One row of the flattened tree fed to the single `VirtualList` below —
 * category headers, kind headers and item rows are ordinary siblings in one
 * array, not nested components each owning their own scroll container.
 * Collapsing a category or kind just omits its children's rows from this
 * array rather than un-mounting a subtree.
 */
type Row =
  | {
      type: "category";
      icon: ReactNode;
      iconColor?: string;
      title: string;
      count: number;
      visible: boolean;
      onToggleVisible: () => void;
      expanded: boolean;
      onToggleExpanded: () => void;
    }
  | {
      type: "kind";
      kind: string;
      swatch: ReactNode;
      count: number;
      visible: boolean;
      onToggleVisible: () => void;
      expanded: boolean;
      onToggleExpanded: () => void;
    }
  | {
      type: "item";
      item: Item;
      /** Indented one extra step, under a kind header, rather than directly under a category header (Nodes/Links items vs. Raingages/Subcatchments ones). */
      nested: boolean;
    };

/**
 * Sidebar list of every element, grouped the same way the real SWMM GUI's
 * Data Browser does (Raingages / Subcatchments / Nodes / Links), with
 * Nodes/Links further split by kind — each kind showing the same shape/color
 * its elements render as on the map, so the tree doubles as a legend. Each
 * group (and each kind within Nodes/Links) can be collapsed and hidden from
 * the map independently. `visibleKinds` is keyed by kind, not category,
 * since a project's single category can mix a handful of dividers into
 * thousands of junctions, and decluttering the map by just the crowded one
 * matters. The category-level eye is a convenience that sets every kind
 * under it at once.
 *
 * The whole tree — every category header, kind header and item row, in
 * expand-respecting document order — is flattened into one `Row[]` and
 * rendered by a single `VirtualList`, not one per category/kind: giving each
 * category its own independent scroll container meant some scrolled
 * internally while others didn't, nested inside the panel's own outer
 * scrollbar — two scroll experiences stacked on top of each other.
 */
export function ElementBrowser({
  project,
  selected,
  multiSelected,
  onSelect,
  visibleKinds,
  onToggleKindVisibility,
  placeholderNames,
}: {
  project: SwmmProject;
  selected: ElementRef | null;
  /** Shift-click multi-selection. Optional so existing callers/tests keep compiling without wiring it up — App.tsx's real usage always passes it. */
  multiSelected?: ElementRef[];
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void;
  /** Keyed by element kind, not category — see this component's own header. */
  visibleKinds: Readonly<Record<string, boolean>>;
  /** Flips one kind's visibility when `explicit` is omitted; sets it to exactly `explicit` when given — the category-level "set every kind the same way" buttons call this once per kind in a loop and need every call to land on the same value regardless of its own prior state. */
  onToggleKindVisibility: (kind: string, explicit?: boolean) => void;
  /** Element names a GIS import left with at least one field still at its default — rendered as a small marker next to the matching row. */
  placeholderNames?: Set<string>;
}) {
  const t = useT();
  const [navRef, listHeight] = useAvailableHeight();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedKinds, setExpandedKinds] = useState<Record<string, boolean>>({});
  const [filterQuery, setFilterQuery] = useState("");

  /** A real project can run into the thousands of elements, so even one
   * unified scrollbar over that many rows is a near-endless scroll with no
   * way to jump to a specific element. Categories start expanded (only ever
   * a handful of rows each, at most one per kind); kinds start collapsed
   * (these are the rows that can run into the thousands). Typing here
   * overrides both entirely: every category/kind with at least one match
   * forces itself open, showing only the matching rows. */
  const query = filterQuery.trim().toLowerCase();
  const isFiltering = query.length > 0;
  // Rebuilding every row of a real (thousands-of-element) project on EVERY
  // render — including one triggered by something wholly unrelated, like a
  // PropertyPanel keystroke on a currently-selected element — used to cost a
  // full O(project size) re-walk each time. Memoized on everything this
  // actually reads; `onToggleKindVisibility` is already `useCallback`'d by
  // App.tsx (stable identity), so it's safe to depend on directly.
  const rows: Row[] = useMemo(() => {
    function matchingItems(items: Item[]): Item[] {
      return isFiltering ? items.filter((item) => item.name.toLowerCase().includes(query)) : items;
    }

    const isCategoryExpanded = (key: string) => isFiltering || expandedCategories[key] !== false;
    const isKindExpanded = (kind: string) => isFiltering || expandedKinds[kind] === true;
    const toggleCategoryExpanded = (key: string) =>
      setExpandedCategories((c) => ({ ...c, [key]: !isCategoryExpanded(key) }));
    const toggleKindExpanded = (kind: string) =>
      setExpandedKinds((k) => ({ ...k, [kind]: !isKindExpanded(kind) }));

    /** All-or-nothing category eye: visible only while EVERY one of `kinds` currently is; clicking it sets all of them to the opposite of that. */
    function allKindsVisible(kinds: readonly string[]): boolean {
      return kinds.every((kind) => visibleKinds[kind] !== false);
    }
    function toggleAllKinds(kinds: readonly string[]) {
      const next = !allKindsVisible(kinds);
      for (const kind of kinds) onToggleKindVisibility(kind, next);
    }

    const nodesByKind = new Map<string, Item[]>();
    for (const kind of NODE_KIND_ORDER) nodesByKind.set(kind, []);
    for (const n of project.nodes) {
      nodesByKind
        .get(n.kind)
        ?.push({ name: n.name, ref: { category: "node" as const, kind: n.kind, name: n.name } });
    }

    const linksByKind = new Map<string, Item[]>();
    for (const kind of LINK_KIND_ORDER) linksByKind.set(kind, []);
    for (const l of project.links) {
      linksByKind
        .get(l.kind)
        ?.push({ name: l.name, ref: { category: "link" as const, kind: l.kind, name: l.name } });
    }

    const rows: Row[] = [];

    function pushFlatCategory(key: {
      category: string;
      icon: ReactNode;
      iconColor: string;
      title: string;
      items: Item[];
    }) {
      const items = matchingItems(key.items);
      if (items.length === 0) return;
      // Raingages/Subcatchments have no kind sub-level of their own — this one
      // header row doubles as both category header and (single) kind header,
      // so it defaults to collapsed like a real kind row does, not expanded
      // like a Nodes/Links category header — a large Subcatchments count would
      // otherwise dump its whole item list open by default.
      const expanded = isKindExpanded(key.category);
      rows.push({
        type: "category",
        icon: key.icon,
        iconColor: key.iconColor,
        title: key.title,
        count: items.length,
        visible: visibleKinds[key.category] !== false,
        onToggleVisible: () => onToggleKindVisibility(key.category),
        expanded,
        onToggleExpanded: () => toggleKindExpanded(key.category),
      });
      if (!expanded) return;
      for (const item of items) rows.push({ type: "item", item, nested: false });
    }

    function pushKindGroupCategory(def: {
      category: string;
      icon: ReactNode;
      title: string;
      kinds: { kind: string; items: Item[]; swatch: ReactNode }[];
    }) {
      const matchingKinds = def.kinds
        .map((g) => ({ ...g, items: matchingItems(g.items) }))
        .filter((g) => g.items.length > 0);
      const totalCount = matchingKinds.reduce((n, g) => n + g.items.length, 0);
      if (totalCount === 0) return;
      const kindNames = def.kinds.map((g) => g.kind);
      const expanded = isCategoryExpanded(def.category);
      rows.push({
        type: "category",
        icon: def.icon,
        title: def.title,
        count: totalCount,
        visible: allKindsVisible(kindNames),
        onToggleVisible: () => toggleAllKinds(kindNames),
        expanded,
        onToggleExpanded: () => toggleCategoryExpanded(def.category),
      });
      if (!expanded) return;
      for (const g of matchingKinds) {
        const kindExpanded = isKindExpanded(g.kind);
        rows.push({
          type: "kind",
          kind: g.kind,
          swatch: g.swatch,
          count: g.items.length,
          visible: visibleKinds[g.kind] !== false,
          onToggleVisible: () => onToggleKindVisibility(g.kind),
          expanded: kindExpanded,
          onToggleExpanded: () => toggleKindExpanded(g.kind),
        });
        if (!kindExpanded) continue;
        for (const item of g.items) rows.push({ type: "item", item, nested: true });
      }
    }

    pushFlatCategory({
      category: "raingage",
      icon: <RaingageIcon />,
      iconColor: RAINGAGE_COLOR,
      title: t("elementBrowser.raingages"),
      items: project.raingages.map((g) => ({
        name: g.name,
        ref: { category: "raingage" as const, kind: "raingage", name: g.name },
      })),
    });
    pushFlatCategory({
      category: "subcatchment",
      icon: <SubcatchmentIcon />,
      iconColor: SUBCATCHMENT_COLOR,
      title: t("elementBrowser.subcatchments"),
      items: project.subcatchments.map((s) => ({
        name: s.name,
        ref: { category: "subcatchment" as const, kind: "subcatchment", name: s.name },
      })),
    });
    pushKindGroupCategory({
      category: "node",
      icon: <NodeIcon />,
      title: t("elementBrowser.nodes"),
      kinds: NODE_KIND_ORDER.map((kind) => ({
        kind,
        items: nodesByKind.get(kind) ?? [],
        swatch: <NodeKindSwatch kind={kind} color={NODE_COLOR[kind]} />,
      })),
    });
    pushKindGroupCategory({
      category: "link",
      icon: <LinkIcon />,
      title: t("elementBrowser.links"),
      kinds: LINK_KIND_ORDER.map((kind) => ({
        kind,
        items: linksByKind.get(kind) ?? [],
        swatch: <LinkKindSwatch color={LINK_COLOR[kind]} dash={LINK_DASH[kind]} />,
      })),
    });

    return rows;
  }, [
    project,
    visibleKinds,
    query,
    isFiltering,
    expandedCategories,
    expandedKinds,
    onToggleKindVisibility,
    t,
  ]);

  function renderRow(row: Row) {
    switch (row.type) {
      case "category":
        return (
          <ElementBrowserHeaderRow
            rowClassName="element-browser-row element-browser-group-header"
            toggleClassName="element-browser-group-toggle"
            toggleAriaLabel={t("elementBrowser.toggleCategoryGroup", { category: row.title })}
            expanded={row.expanded}
            onToggleExpanded={row.onToggleExpanded}
            visible={row.visible}
            onToggleVisible={row.onToggleVisible}
          >
            <span
              className="element-browser-group-icon"
              style={row.iconColor ? { color: row.iconColor } : undefined}
            >
              {row.icon}
            </span>
            <h3>
              {row.title} <span className="element-browser-count">({row.count})</span>
            </h3>
          </ElementBrowserHeaderRow>
        );
      case "kind":
        return (
          <ElementBrowserHeaderRow
            rowClassName="element-browser-row element-browser-kind-header"
            toggleClassName="element-browser-kind-toggle"
            toggleAriaLabel={t("elementBrowser.toggleKindGroup", {
              kind: t(rowKindLabelKey(row.kind)),
            })}
            expanded={row.expanded}
            onToggleExpanded={row.onToggleExpanded}
            visible={row.visible}
            onToggleVisible={row.onToggleVisible}
          >
            <span className="element-browser-kind-swatch">{row.swatch}</span>
            <span className="element-browser-kind-label">
              {t(rowKindLabelKey(row.kind))}{" "}
              <span className="element-browser-count">({row.count})</span>
            </span>
          </ElementBrowserHeaderRow>
        );
      case "item": {
        const { item, nested } = row;
        const isPrimary = selected?.category === item.ref.category && selected.name === item.name;
        const isMulti = isMultiSelected(multiSelected, item.ref.category, item.name);
        const className = [
          "element-browser-item",
          nested && "element-browser-item-nested",
          isMulti && "element-browser-item-multi-selected",
          isPrimary && "element-browser-item-selected",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <div className="element-browser-row">
            <button
              className={className}
              aria-pressed={isPrimary || isMulti}
              onClick={(e) => onSelect(item.ref, e.shiftKey)}
            >
              {item.name}
              {placeholderNames?.has(item.name) && (
                <span
                  className="element-browser-placeholder-marker"
                  title={t("elementBrowser.placeholderHint")}
                />
              )}
            </button>
          </div>
        );
      }
    }
  }

  return (
    <nav className="element-browser">
      <div className="element-browser-filter">
        <input
          type="text"
          className="element-browser-filter-input"
          placeholder={t("elementBrowser.filterPlaceholder")}
          aria-label={t("elementBrowser.filterPlaceholder")}
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && filterQuery) {
              e.stopPropagation();
              setFilterQuery("");
            }
          }}
        />
      </div>
      <div className="element-browser-list-wrapper" ref={navRef}>
        {rows.length === 0 && isFiltering ? (
          <p className="element-browser-filter-empty">{t("elementBrowser.filterNoMatches")}</p>
        ) : (
          <VirtualList
            items={rows}
            height={listHeight}
            rowType={(row) => row.type}
            // A stable identity per row, not the array index — expanding/
            // collapsing a category ahead of this one shifts every later
            // row's own index, which used to misattribute focus after a
            // reorder (P1 deep-review, VirtualList.tsx's own index-key gap).
            getKey={(row) =>
              row.type === "category"
                ? `category:${row.title}`
                : row.type === "kind"
                  ? `kind:${row.kind}`
                  : `item:${row.item.ref.kind}:${row.item.name}`
            }
            renderItem={renderRow}
            className="element-browser-list"
          />
        )}
      </div>
    </nav>
  );
}
